import { useEffect, type ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useLocation, matchPath } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { PERMISSIONS, type Permission } from '@zeroth/shared';
import { SignIn, SignUp, SignedIn, SignedOut, RedirectToSignIn } from './lib/clerk';
import { setAuthTokenGetter } from './lib/api';
import { flushPendingPageLoadDiagnostic } from './lib/pageLoadDiagnostics';
import { useAccountStore } from './stores/accountStore';
import { DashboardPageSkeleton } from './features/dashboard/components/DashboardPageSkeleton';
import { usePersistedCacheUserIsolation } from './lib/queryPersister';
import { useZustandUserIsolation } from './lib/zustandUserIsolation';
import { useSessionPageStateUserIsolation } from './hooks/useSessionPageState';
import { PageLoadErrorBoundary } from './components/layout/PageLoadErrorBoundary';
import { PageTitleProvider } from './components/layout/PageTitleProvider';
import { ThemeToggle } from './components/ui/ThemeToggle';
import { buttonVariants } from './components/ui/button';
import { cn } from './lib/utils';
import { clerkAppearance } from './lib/clerkAppearance';
import zerothLogo from './assets/zeroth-logo.png';
import { defaultPathForPermissions } from './lib/permissionLanding';
import { captureSignupTrial, parseTrialTier, readSignupTrial } from './features/onboarding/signupIntent';
import { DeviceAccessGate } from './features/auth/DeviceAccessGate';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex h-[var(--app-viewport-height)] items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
    </div>
  );
}

const GetStartedPage = lazy(() => import('./features/onboarding/GetStartedPage'));

// Lazy loaded pages for code splitting. Import failures should surface to the
// page load error boundary instead of rendering a fake missing-page placeholder.
const ReferralLanding = lazy(() => import('./features/referrals/ReferralLanding').then(m => ({ default: m.ReferralLanding })));
const DashboardPage = lazy(() => import('./features/dashboard/pages/DashboardPage'));
const AgentPage = lazy(() => import('./features/agent/pages/AgentPage'));
const ProductsPage = lazy(() => import('./features/products/pages/ProductsPage'));
const InventoryAuditPage = lazy(() => import('./features/inventory/pages/InventoryAuditPage'));
const ExpirationLotsPage = lazy(() => import('./features/inventory/pages/ExpirationLotsPage'));
const SuppliersPage = lazy(() => import('./features/suppliers/pages/SuppliersPage'));
const ListingsPage = lazy(() => import('./features/listings/pages/ListingsPage'));
const OperationalReadinessPage = lazy(() => import('./features/readiness/pages/OperationalReadinessPage'));
const SettingsRoutes = lazy(() => import('./features/settings/pages/SettingsRoutes'));
const ConnectStoreSuccessPage = lazy(() => import('./features/settings/pages/ConnectStoreSuccessPage'));
const ConnectStoreErrorPage = lazy(() => import('./features/settings/pages/ConnectStoreErrorPage'));
const AmazonLoginRedirectPage = lazy(() => import('./features/settings/pages/AmazonLoginRedirectPage'));
const RestockSuggestionsPage = lazy(() => import('./features/restock/pages/RestockSuggestionsPage'));
const SupplierMappingPage = lazy(() => import('./features/restock/pages/SupplierMappingPage'));
const PurchaseOrdersPage = lazy(() => import('./features/purchase-orders/pages/PurchaseOrdersPage'));
const PurchaseOrderDetailPage = lazy(() => import('./features/purchase-orders/pages/PurchaseOrderDetailPage'));
const CreatePurchaseOrderPage = lazy(() => import('./features/purchase-orders/pages/CreatePurchaseOrderPage'));
const LabelsPage = lazy(() => import('./features/labels/pages/LabelsPage'));
const AlertsPage = lazy(() => import('./features/alerts/pages/AlertsPage'));
const AnalyticsPage = lazy(() => import('./features/analytics/pages/AnalyticsPage'));
const SkuAnalyticsDetailPage = lazy(() => import('./features/analytics/pages/SkuAnalyticsDetailPage'));
const ReportsPage = lazy(() => import('./features/reports/pages/ReportsPage'));
const WorkflowBuilderPage = lazy(() =>
  import('./features/workflow/pages/WorkflowBuilderPage').then((m) => ({
    default: m.WorkflowBuilderPage,
  }))
);
const DraftShipmentsPage = lazy(() =>
  import('./features/shipments/pages/DraftShipmentsPage').then((m) => ({
    default: m.DraftShipmentsPage,
  }))
);
const DraftShipmentDetailPage = lazy(() =>
  import('./features/shipments/pages/DraftShipmentDetailPage').then((m) => ({
    default: m.DraftShipmentDetailPage,
  }))
);
const ActiveShipmentsPage = lazy(() =>
  import('./features/shipments/pages/ActiveShipmentsPage').then((m) => ({
    default: m.ActiveShipmentsPage,
  }))
);
const ConsolidatedShipmentPage = lazy(() =>
  import('./features/shipments/pages/ConsolidatedShipmentPage').then((m) => ({
    default: m.ConsolidatedShipmentPage,
  }))
);
const ShippedShipmentPage = lazy(() =>
  import('./features/shipments/pages/ShippedShipmentPage').then((m) => ({
    default: m.ShippedShipmentPage,
  }))
);
const ShippedHubLayout = lazy(() =>
  import('./features/shipments/pages/ShippedHubPage').then((m) => ({
    default: m.ShippedHubLayout,
  }))
);
const ShippedDefaultRedirect = lazy(() =>
  import('./features/shipments/pages/ShippedHubPage').then((m) => ({
    default: m.ShippedDefaultRedirect,
  }))
);
const LegacyMasterShipmentRedirect = lazy(() =>
  import('./features/shipments/pages/WorkflowRouteRedirects').then((m) => ({
    default: m.LegacyMasterShipmentRedirect,
  }))
);
const LegacyWorkingShipmentRedirect = lazy(() =>
  import('./features/shipments/pages/WorkflowRouteRedirects').then((m) => ({
    default: m.LegacyWorkingShipmentRedirect,
  }))
);
const LegacyShippedShipmentRedirect = lazy(() =>
  import('./features/shipments/pages/WorkflowRouteRedirects').then((m) => ({
    default: m.LegacyShippedShipmentRedirect,
  }))
);
const LegacyOutboundFreightRedirect = lazy(() =>
  import('./features/shipments/pages/WorkflowRouteRedirects').then((m) => ({
    default: m.LegacyOutboundFreightRedirect,
  }))
);
const LegacyBuilderRedirect = lazy(() =>
  import('./features/shipments/pages/WorkflowRouteRedirects').then((m) => ({
    default: m.LegacyBuilderRedirect,
  }))
);
const SolicitationDashboardPage = lazy(() =>
  import('./features/solicitations/pages/SolicitationDashboardPage').then((m) => ({
    default: m.SolicitationDashboardPage,
  }))
);
const CampaignManagerPage = lazy(() =>
  import('./features/solicitations/pages/CampaignManagerPage').then((m) => ({
    default: m.CampaignManagerPage,
  }))
);
const AlertCenterPage = lazy(() =>
  import('./features/alerts/pages/AlertCenterPage').then((m) => ({
    default: m.AlertCenterPage,
  }))
);
const ResearchListsPage = lazy(() =>
  import('./features/research/pages/ResearchListsPage').then((m) => ({
    default: m.ResearchListsPage,
  }))
);
const ResearchUploadPage = lazy(() =>
  import('./features/research/pages/ResearchUploadPage').then((m) => ({
    default: m.ResearchUploadPage,
  }))
);
const ResearchLiveScannerPage = lazy(() =>
  import('./features/research/pages/ResearchLiveScannerPage').then((m) => ({
    default: m.ResearchLiveScannerPage,
  }))
);
const MobileResearchScannerPage = lazy(() =>
  import('./features/research/pages/MobileResearchScannerPage').then((m) => ({
    default: m.MobileResearchScannerPage,
  }))
);
const ResearchDetailPage = lazy(() =>
  import('./features/research/pages/ResearchDetailPage').then((m) => ({
    default: m.ResearchDetailPage,
  }))
);
const InviteAcceptPage = lazy(() =>
  import('./features/account/pages/InviteAcceptPage').then((m) => ({
    default: m.InviteAcceptPage,
  }))
);
const OutboundFreightQueuePage = lazy(() =>
  import('./features/outboundFreight/pages/OutboundFreightQueuePage').then((m) => ({
    default: m.OutboundFreightQueuePage,
  }))
);
const OutboundFreightPage = lazy(() =>
  import('./features/outboundFreight/pages/OutboundFreightPage').then((m) => ({
    default: m.OutboundFreightPage,
  }))
);

// Protected route wrapper - requires authentication
function ProtectedRoute() {
  return (
    <>
      <SignedIn>
        <DeviceAccessGate><Outlet /></DeviceAccessGate>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function HomeRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <PageLoader />;

  return (
    <Navigate
      to={isSignedIn ? '/dashboard' : { pathname: '/sign-up', search: location.search, hash: location.hash }}
      replace
    />
  );
}

type AuthMode = 'sign-up' | 'sign-in';

function AuthNavigation({ mode }: { mode: AuthMode }) {
  const location = useLocation();

  return (
    <nav aria-label="Account access" className="grid grid-cols-2 gap-2">
      {(['sign-up', 'sign-in'] as const).map((target) => {
        const active = mode === target;
        return (
          <Link
            key={target}
            to={{
              pathname: active ? location.pathname : `/${target}`,
              search: location.search,
              hash: location.hash,
            }}
            aria-current={active ? 'page' : undefined}
            className={cn(buttonVariants({
              variant: active ? 'primary' : 'outline',
              size: 'lg',
              className: 'min-h-[calc(48px*var(--app-density-scale-inverse))]',
            }), !active && 'bg-card text-card-foreground shadow-sm')}
          >
            {target === 'sign-up' ? 'Sign up' : 'Sign in'}
          </Link>
        );
      })}
    </nav>
  );
}

// Auth layout for sign-in and sign-up pages
function AuthLayout({ mode }: { mode: AuthMode }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tier = params.has('trial') ? parseTrialTier(params.get('trial')) ?? 'STARTER' : readSignupTrial();
  const fallbackRedirectUrl = `/get-started?trial=${tier.toLowerCase()}`;
  useEffect(() => { captureSignupTrial(location.search); }, [location.search]);
  return (
    <div className="min-h-[var(--app-viewport-height)] bg-background text-foreground">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <main className="flex min-h-[var(--app-viewport-height)] items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden">
              <img
                src={zerothLogo}
                alt="Zeroth logo"
                className="h-24 w-24 max-w-none shrink-0 object-contain"
              />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">Zeroth</h1>
              <p className="text-sm text-muted-foreground">
                {mode === 'sign-up' ? 'Create your Zeroth account' : 'Sign in to your Zeroth account'}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <AuthNavigation mode={mode} />
            {mode === 'sign-up'
              ? <SignUp routing="path" path="/sign-up" fallbackRedirectUrl={fallbackRedirectUrl} appearance={clerkAppearance} />
              : <SignIn routing="path" path="/sign-in" fallbackRedirectUrl={fallbackRedirectUrl} appearance={clerkAppearance} />}
          </div>
        </div>
      </main>
    </div>
  );
}

// Lazy load AppShell
const AppShell = lazy(() => import('./components/layout/AppShell'));

// App shell layout for authenticated pages
function AppShellLayout() {
  const location = useLocation();
  return (
    <Suspense fallback={<PageLoader />}>
      <AppShell contentLayout={matchPath({ path: '/restock/suggestions', end: true }, location.pathname) ? 'workspace' : 'page'}>
        <PageLoadErrorBoundary resetKey={`${location.pathname}${location.search}`} recoverInPlace>
          <Outlet />
        </PageLoadErrorBoundary>
      </AppShell>
    </Suspense>
  );
}

function RequireAnyPermission({
  permissions,
  children,
}: {
  permissions: Permission[];
  children: ReactElement;
}) {
  const membership = useAccountStore((state) => state.membership);
  const granted = useAccountStore((state) => state.permissions);

  if (!membership) {
    return <PageLoader />;
  }

  if (!permissions.some((permission) => granted.includes(permission))) {
    return <Navigate to={defaultPathForPermissions(granted)} replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}`;

  return (
    <PageLoadErrorBoundary resetKey={resetKey}>
      <Routes>
        {/* Public referral link landing; the partner portal itself is a separate app (packages/partner-portal). */}
        <Route path="/r/:code" element={<Suspense fallback={<PageLoader />}><ReferralLanding /></Suspense>} />
        {/* Public auth routes */}
        <Route path="/" element={<HomeRedirect />} />
        <Route
          path="/sign-in/*"
          element={
            <AuthLayout mode="sign-in" />
          }
        />
        <Route
          path="/sign-up/*"
          element={
            <AuthLayout mode="sign-up" />
          }
        />
        <Route
          path="/research/mobile-scan"
          element={<Suspense fallback={<PageLoader />}><MobileResearchScannerPage /></Suspense>}
        />

        {/* Protected app routes */}
        <Route element={<ProtectedRoute />}>
          <Route
            path="invite/:token"
            element={
              <Suspense fallback={<PageLoader />}>
                <InviteAcceptPage />
              </Suspense>
            }
          />
          <Route element={<AppShellLayout />}>
            {/* Main application routes */}
            <Route path="get-started" element={<GetStartedPage />} />
            <Route
              path="dashboard"
              element={
                <RequireAnyPermission permissions={[PERMISSIONS.DASHBOARD_VIEW]}>
                  <Suspense fallback={<DashboardPageSkeleton />}>
                    <DashboardPage />
                  </Suspense>
                </RequireAnyPermission>
              }
            />
            <Route path="agent" element={<RequireAnyPermission permissions={[PERMISSIONS.AI_USE]}><AgentPage /></RequireAnyPermission>} />
            <Route path="products" element={<RequireAnyPermission permissions={[PERMISSIONS.PRODUCTS_VIEW]}><ProductsPage /></RequireAnyPermission>} />

            <Route path="inventory">
              <Route path="audit" element={<RequireAnyPermission permissions={[PERMISSIONS.INVENTORY_VIEW]}><InventoryAuditPage /></RequireAnyPermission>} />
              <Route path="expiration-lots" element={<RequireAnyPermission permissions={[PERMISSIONS.INVENTORY_VIEW]}><ExpirationLotsPage /></RequireAnyPermission>} />
            </Route>
            
            {/* Restock Module */}
            <Route path="restock">
              <Route index element={<Navigate to="/restock/suggestions" replace />} />
              <Route path="suggestions" element={<RequireAnyPermission permissions={[PERMISSIONS.RESTOCK_VIEW]}><RestockSuggestionsPage /></RequireAnyPermission>} />
              <Route path="supplier-mapping" element={<RequireAnyPermission permissions={[PERMISSIONS.AI_USE]}><SupplierMappingPage /></RequireAnyPermission>} />
              <Route path="opportunities" element={<Navigate to="/research" replace />} />
              <Route path="pos" element={<RequireAnyPermission permissions={[PERMISSIONS.PURCHASE_ORDERS_VIEW]}><PurchaseOrdersPage /></RequireAnyPermission>} />
              <Route path="pos/create" element={<RequireAnyPermission permissions={[PERMISSIONS.PURCHASE_ORDERS_MANAGE]}><CreatePurchaseOrderPage /></RequireAnyPermission>} />
              <Route path="pos/:id" element={<RequireAnyPermission permissions={[PERMISSIONS.PURCHASE_ORDERS_VIEW]}><PurchaseOrderDetailPage /></RequireAnyPermission>} />
              <Route path="suppliers" element={<RequireAnyPermission permissions={[PERMISSIONS.SUPPLIERS_VIEW]}><SuppliersPage /></RequireAnyPermission>} />
            </Route>
            
            {/* Repricer Module */}
            <Route path="repricer">
              <Route index element={<Navigate to="/repricer/listings" replace />} />
              <Route path="listings" element={<RequireAnyPermission permissions={[PERMISSIONS.REPRICER_MANAGE]}><ListingsPage /></RequireAnyPermission>} />
            </Route>
            
            {/* Workflows Module */}
            <Route path="workflows">
              <Route index element={<Navigate to="/workflows/draft-shipments" replace />} />
              <Route path="builder" element={<LegacyBuilderRedirect />} />
              <Route path="draft-shipments" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW, PERMISSIONS.SHIPMENTS_CREATE, PERMISSIONS.SHIPMENTS_MANAGE]}><DraftShipmentsPage /></RequireAnyPermission>} />
              <Route path="draft-shipments/new" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_CREATE, PERMISSIONS.SHIPMENTS_MANAGE]}><WorkflowBuilderPage /></RequireAnyPermission>} />
              <Route path="draft-shipments/:id" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW, PERMISSIONS.SHIPMENTS_CREATE, PERMISSIONS.SHIPMENTS_MANAGE]}><DraftShipmentDetailPage /></RequireAnyPermission>} />
              <Route path="master" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><LegacyMasterShipmentRedirect /></RequireAnyPermission>} />
              <Route path="master/:planId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><LegacyMasterShipmentRedirect /></RequireAnyPermission>} />
              <Route path="master/:planId/:shipmentId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><LegacyMasterShipmentRedirect /></RequireAnyPermission>} />
              <Route path="working-shipments" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ActiveShipmentsPage /></RequireAnyPermission>} />
              <Route path="working-shipments/plan/:planId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ActiveShipmentsPage /></RequireAnyPermission>} />
              <Route path="working-shipments/plan/:planId/master" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ConsolidatedShipmentPage /></RequireAnyPermission>} />
              <Route path="working-shipments/plan/:planId/destination/:shipmentId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ConsolidatedShipmentPage /></RequireAnyPermission>} />
              <Route path="working-shipments/:id" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><LegacyWorkingShipmentRedirect /></RequireAnyPermission>} />
              <Route path="shipped" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW, PERMISSIONS.SHIPPING_MANAGE]}><ShippedHubLayout /></RequireAnyPermission>}>
                <Route index element={<ShippedDefaultRedirect />} />
                <Route path="fba" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ActiveShipmentsPage stage="SHIPPED" /></RequireAnyPermission>} />
                <Route path="fba/plan/:planId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ActiveShipmentsPage stage="SHIPPED" /></RequireAnyPermission>} />
                <Route path="fba/plan/:planId/destination/:shipmentId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><ShippedShipmentPage /></RequireAnyPermission>} />
                <Route path="freight" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPPING_MANAGE]}><OutboundFreightQueuePage /></RequireAnyPermission>} />
                <Route path="freight/:shipmentId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPPING_MANAGE]}><OutboundFreightPage /></RequireAnyPermission>} />
              </Route>
              <Route path="shipped/:id" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPMENTS_VIEW]}><LegacyShippedShipmentRedirect /></RequireAnyPermission>} />
              <Route path="labels" element={<RequireAnyPermission permissions={[PERMISSIONS.LABELS_PRINT]}><LabelsPage /></RequireAnyPermission>} />
            </Route>

            {/* Seller-approved outbound LTL and Small Parcel. This is separate from FBA inbound. */}
            <Route path="shipping/outbound">
              <Route index element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPPING_MANAGE]}><LegacyOutboundFreightRedirect /></RequireAnyPermission>} />
              <Route path=":shipmentId" element={<RequireAnyPermission permissions={[PERMISSIONS.SHIPPING_MANAGE]}><LegacyOutboundFreightRedirect /></RequireAnyPermission>} />
            </Route>
            
            {/* Solicitations Module */}
            <Route path="solicitations">
              <Route index element={<RequireAnyPermission permissions={[PERMISSIONS.SOLICITATIONS_VIEW]}><SolicitationDashboardPage /></RequireAnyPermission>} />
              <Route path="campaigns/:id" element={<RequireAnyPermission permissions={[PERMISSIONS.SOLICITATIONS_VIEW]}><CampaignManagerPage /></RequireAnyPermission>} />
            </Route>

            {/* Product Research */}
            <Route path="research">
              <Route index element={<RequireAnyPermission permissions={[PERMISSIONS.RESEARCH_VIEW]}><ResearchListsPage /></RequireAnyPermission>} />
              <Route path="new" element={<RequireAnyPermission permissions={[PERMISSIONS.RESEARCH_ENRICH]}><ResearchUploadPage /></RequireAnyPermission>} />
              <Route path="upload" element={<RequireAnyPermission permissions={[PERMISSIONS.RESEARCH_ENRICH]}><ResearchUploadPage /></RequireAnyPermission>} />
              <Route path="live" element={<RequireAnyPermission permissions={[PERMISSIONS.RESEARCH_ENRICH]}><ResearchLiveScannerPage /></RequireAnyPermission>} />
              <Route path=":id" element={<RequireAnyPermission permissions={[PERMISSIONS.RESEARCH_VIEW]}><ResearchDetailPage /></RequireAnyPermission>} />
            </Route>

            {/* Alert Center */}
            <Route path="alert-center" element={<RequireAnyPermission permissions={[PERMISSIONS.ALERTS_VIEW]}><AlertCenterPage /></RequireAnyPermission>} />

            {/* Alerts (legacy) */}
            <Route path="alerts" element={<RequireAnyPermission permissions={[PERMISSIONS.ALERTS_VIEW]}><AlertsPage /></RequireAnyPermission>} />

            {/* Operational Readiness */}
            <Route path="readiness" element={<RequireAnyPermission permissions={[PERMISSIONS.READINESS_VIEW]}><OperationalReadinessPage /></RequireAnyPermission>} />

            {/* Analytics */}
            <Route path="analytics" element={<RequireAnyPermission permissions={[PERMISSIONS.ANALYTICS_VIEW]}><AnalyticsPage /></RequireAnyPermission>} />
            <Route path="analytics/sku/:sku" element={<RequireAnyPermission permissions={[PERMISSIONS.ANALYTICS_VIEW]}><SkuAnalyticsDetailPage /></RequireAnyPermission>} />

            {/* Reports */}
            <Route path="reports" element={<RequireAnyPermission permissions={[PERMISSIONS.REPORTS_VIEW]}><ReportsPage /></RequireAnyPermission>} />

            {/* Settings areas (store, operations, organization, profile) with OAuth callback routes */}
            <Route path="settings/stores/connect/success" element={<ConnectStoreSuccessPage />} />
            <Route path="settings/stores/connect/error" element={<ConnectStoreErrorPage />} />
            <Route path="settings/stores/connect/amazon-login" element={<AmazonLoginRedirectPage />} />
            <Route path="settings/*" element={<SettingsRoutes />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Route>
      </Routes>
    </PageLoadErrorBoundary>
  );
}

// Main App component with routing
export function App() {
  const { getToken } = useAuth();
  const { isLoaded: isUserLoaded, user } = useUser();
  const userId = user?.id ?? null;

  // Set up auth token getter for API client
  useEffect(() => {
    setAuthTokenGetter(async () => {
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
  }, [getToken]);

  useEffect(() => {
    if (isUserLoaded && userId) {
      void flushPendingPageLoadDiagnostic();
    }
  }, [isUserLoaded, userId]);

  usePersistedCacheUserIsolation(userId, isUserLoaded);
  useZustandUserIsolation(userId, isUserLoaded);
  useSessionPageStateUserIsolation(userId, isUserLoaded);

  return (
    <BrowserRouter>
      <PageTitleProvider userId={userId}>
        <AppRoutes />
      </PageTitleProvider>
    </BrowserRouter>
  );
}

export default App;
